import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import { InventoryLocationOption } from "../models/InventoryLocation";
import { StorefrontThemeSettings } from "../models/StorefrontThemeSettings";
import { UserSetting } from "../models/UserSetting";
import { VendorSkuRule } from "../models/VendorSkuRule";

export default class SettingStore {
    // Using javascript Map object to store our key-value paired data, instead of a regular array, since it will give us more power/options
    settingRegistry = new Map<string, UserSetting>();
    currentSettings: UserSetting | undefined = undefined;
    loadingInitial = true;
    loadingUpdate = false;
    themeSettings: StorefrontThemeSettings | undefined = undefined;
    loadingThemeSettings = false;
    inventoryLocations: InventoryLocationOption[] = [];
    loadingInventoryLocations = false;
    vendorSkuRules: VendorSkuRule[] = [];
    loadingVendorSkuRules = false;

    constructor() {
        makeAutoObservable(this)
    }

    get settings() {
        return Array.from(this.settingRegistry.values());
    }

    loadSettings = async () => {
        //this.setLoadingInitial(true);

        try {
            const settings = await agent.Settings.details();

            if (settings) {
                this.setSetting(settings);
                this.setCurrentSettings(settings);

                if (settings.inventoryLocationIds && this.inventoryLocations.length > 0) {
                    this.applyInventorySelection(settings.inventoryLocationIds);
                }
            }

            this.setLoadingInitial(false);

        } catch (error) {
            console.log(error);
            this.setLoadingInitial(false);
        }
    }

    loadInventoryLocations = async () => {
        this.loadingInventoryLocations = true;

        try {
            const locations = await agent.Settings.inventoryLocations();

            runInAction(() => {
                this.inventoryLocations = locations;
                if (this.currentSettings?.inventoryLocationIds) {
                    this.applyInventorySelection(this.currentSettings.inventoryLocationIds);
                }
            });
        } catch (error) {
            console.log(error);
        } finally {
            runInAction(() => {
                this.loadingInventoryLocations = false;
            });
        }
    }

    updateSettings = async (setting: UserSetting) => {
        this.loadingUpdate = true;

        try {
            await agent.Settings.update(setting);

            runInAction(() => {
                // re-load fitments
                this.loadSettings();
                this.loadInventoryLocations();
            });

            this.loadingUpdate = false;
            return(true);
        }
        catch (error) {            
            this.loadingUpdate = false;
            return(false);             
        }
    }

    loadThemeSettings = async () => {
        this.loadingThemeSettings = true;

        try {
            const theme = await agent.Settings.theme();

            runInAction(() => {
                this.themeSettings = theme;
            });
        } catch (error) {
            console.log(error);
        } finally {
            runInAction(() => {
                this.loadingThemeSettings = false;
            });
        }
    }

    updateThemeSettings = async (setting: StorefrontThemeSettings) => {
        this.loadingUpdate = true;

        try {
            const updated = await agent.Settings.updateTheme(setting);

            runInAction(() => {
                this.themeSettings = updated;
                this.loadingUpdate = false;
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this.loadingUpdate = false;
            });

            return false;
        }
    }

    loadVendorSkuRules = async () => {
        this.loadingVendorSkuRules = true;

        try {
            const rules = await agent.Settings.vendorSkuRules();

            runInAction(() => {
                this.vendorSkuRules = rules;
            });
        } catch (error) {
            console.log(error);
        } finally {
            runInAction(() => {
                this.loadingVendorSkuRules = false;
            });
        }
    }

    updateVendorSkuRules = async (rules: VendorSkuRule[]) => {
        this.loadingUpdate = true;

        try {
            const updatedRules = await agent.Settings.updateVendorSkuRules(rules);

            runInAction(() => {
                this.vendorSkuRules = updatedRules;
                this.loadingUpdate = false;
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this.loadingUpdate = false;
            });

            return false;
        }
    }

    setVendorSkuPrefix = (shopifyVendor: string, skuPrefix: string) => {
        this.vendorSkuRules = this.vendorSkuRules.map(rule =>
            rule.shopifyVendor === shopifyVendor ? { ...rule, skuPrefix } : rule
        );
    }

    setVendorSkuNormalize = (shopifyVendor: string, normalizeSku: boolean) => {
        this.vendorSkuRules = this.vendorSkuRules.map(rule =>
            rule.shopifyVendor === shopifyVendor ? { ...rule, normalizeSku } : rule
        );
    }

    private setSetting = (setting: UserSetting) => {
        const key = setting.id !== undefined ? setting.id.toString() : "current";
        this.settingRegistry.set(key, setting);
    }

    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    }

    private setCurrentSettings = (setting: UserSetting) => {
        this.currentSettings = setting;
    }

    setInventoryLocationSelected = (id: number, selected: boolean) => {
        this.inventoryLocations = this.inventoryLocations.map(location =>
            location.id === id ? { ...location, selected } : location
        );
    }

    get selectedInventoryLocationIds() {
        return this.inventoryLocations.filter(location => location.selected).map(location => location.id);
    }

    private applyInventorySelection = (ids: number[]) => {
        const idSet = new Set(ids);
        this.inventoryLocations = this.inventoryLocations.map(location => ({
            ...location,
            selected: idSet.has(location.id)
        }));
    }
}
